"use client";

import { WIDTH_XL } from "@constants/global";
import { useWindowWidth } from "@react-hook/window-size";
import { IMenuList } from "components/Layout/ManagerProLayout/utils";
import { useIsScreenMd } from "hooks/useIsScreenMd";
import { useRouter } from "next/router";
import React, { useEffect, useState } from "react";
import { IRoute } from "routes/types";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from "shadcn/components/ui/tooltip";
import { CircleActionButton, CircleActionButtonProps, ModuleIcon } from "components/ui/v2";
import { useDesktopSidebarStore } from "stores/sidebar/useDesktopSidebarStore";

interface MenuItemProps {
  item: IMenuList;
  onClick: (path: string) => void;
  checkSubRoutesRoleGuard: (routes: IRoute[]) => boolean;
  setIsHoverItem: (value: boolean) => void;
  render?: boolean;
}

export default React.memo(function MenuItem(props: MenuItemProps) {
  const {
    item,
    onClick,
    checkSubRoutesRoleGuard,
    setIsHoverItem,
    render = true
  } = props;
  const router = useRouter();
  const { isExpanded } = useDesktopSidebarStore();
  const [renderText, setRenderText] = useState(true);
  const isScreenMd = useIsScreenMd();
  const wWidth = useWindowWidth();
  const is720 = wWidth < WIDTH_XL;

  useEffect(() => {
    if (isExpanded) {
      setRenderText(true);
    }
    if (!isExpanded && isScreenMd) {
      setTimeout(() => {
        setRenderText(false);
      }, 200);
    }
  }, [isExpanded, isScreenMd, is720]);

  const handleMouseEnter = () => {
    setIsHoverItem(true); // Actualizar estado a 'close' al salir del hover
  };

  const resolveTone = (): NonNullable<CircleActionButtonProps["tone"]> => {
    if (!item.route?.path) {
      return "ocean";
    }
    if (item.route.path.includes("explore")) return "ocean";
    if (item.route.path.includes("challenge")) return "violet";
    if (item.route.path.includes("marketplace")) return "sunset";
    return "emerald";
  };

  const isActive = router.pathname === item.route?.path;
  const useCircleActionButton = !isExpanded && isScreenMd;

  if (!render) return null;

  return item.route && !checkSubRoutesRoleGuard([item.route]) ? (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger
          className={`flex h-sidebar-button w-full items-center gap-4 pl-6 ${
            useCircleActionButton
              ? "justify-center pl-0 bg-transparent hover:bg-transparent dark:hover:bg-transparent"
              : `${router.pathname !== item.route.path && "hover:bg-gray-100 hover:dark:bg-slate-800"} ${router.pathname === item.route.path && "active bg-gray-200 dark:bg-gray-800"}`
          } `}
          onClick={() => item.route && onClick(item.route.path)}
          onMouseOver={handleMouseEnter}
        >
          <>
            {useCircleActionButton ? (
              <CircleActionButton
                as="div"
                context="sidebar"
                tone={resolveTone()}
                active={isActive}
              >
                <ModuleIcon
                  module={item.moduleIcon}
                  path={item.urlIcon}
                  size={28}
                  className="h-sidebar-icon w-sidebar-icon"
                  aria-label={item.name}
                />
              </CircleActionButton>
            ) : (
              <ModuleIcon
                module={item.moduleIcon}
                path={item.urlIcon}
                size={28}
                className="h-sidebar-icon w-sidebar-icon"
                aria-label={item.name}
              />
            )}

            {renderText && (
              <span className="block overflow-hidden truncate text-wrap text-left text-sidebar">
                {item.name}
              </span>
            )}
          </>
        </TooltipTrigger>
        {!isExpanded && isScreenMd && (
          <TooltipContent
            side="right"
            className="dark:border-gray-600 dark:bg-gray-900"
          >
            <p> {item.name}</p>
          </TooltipContent>
        )}
      </Tooltip>
    </TooltipProvider>
  ) : null;
});
